import { ref, reactive, toRefs, nextTick, computed } from 'vue'
import { FIXED_DATA } from '../data/table-cols.js'
import { isArray } from '@/common/js/utils'
import { div } from '@/common/js/api'
import { toThousands, toPercentFixed } from '@/common/js/filters'
import { getPtomPlanData, getPtomPlanToutiaoRaiseStatus, getPtomWarningPlanCounts } from '@/api/ptom.data.model'
import { MEDIAS } from '@/common/js/constants.js'
import { TOUTIAO_RAISE_DRAWER_TYPE } from '../components/detail.data.js'

export function useTableData({ buildQueryParams, queryEarlyWarningPlanCount, queryRaiseStatus }) {
  const planDataTableRef = ref(null)
  const state = reactive({
    tableList: [],
    sumdata: {},
    loading: false,
    pageIndex: 1,
    pageSizePrivate: 20,
    total: 0,
    sort: '',
    order: '',

    paramsCache: null
  })

  const getData = () => {
    if (state.loading) return

    const { baseParams, customParams } = buildQueryParams()
    const finalParams = {
      ...customParams,
      ...baseParams,
      pageId: state.pageIndex,
      pageSize: state.pageSizePrivate
    }

    state.paramsCache = JSON.parse(JSON.stringify(finalParams))
    state.loading = true

    queryEarlyWarningPlanCount()

    // Fetch table data
    getPtomPlanData(finalParams).then((data) => {
      if (typeof data !== 'symbol') state.loading = false
      if (!data) return

      const { list, page_info, sum_data } = data
      if (isArray(list) && page_info && sum_data) {
        state.total = page_info.total_number || 0
        state.sumdata = sum_data
        const tableList = list.map((ret) => {
          if (ret.deductSwitch === 1 && ret.deductInfo) {
            const { deductNum, deductUnitNum } = ret.deductInfo
            ret.deductRate = div(deductNum, deductUnitNum).toFixed(2)
          } else {
            ret.deductRate = 0
          }
          ret.optStatus = Number(ret.optStatus === 1)

          const { bookProjectBudget, bookAdBudget } = ret
          return {
            ...ret,
            showBudgetActionPopover: false,
            showBudgetEditPopover: false,
            showAdBudgetActionPopover: false,
            showAdBudgetEditPopover: false,
            reserveBudgetRadio: ret.bookProjectBudget > 0 ? 1 : 0,
            reserveAdBudgetRadio: ret.bookAdBudget > 0 ? 1 : 0,
            bookAdBudgetDup: ret.bookAdBudget,
            bookProjectBudgetDup: ret.bookProjectBudget,
            isProjectReserveModifyBudget: bookProjectBudget > 0 || bookProjectBudget === 0,
            isAdReserveModifyBudget: bookAdBudget > 0 || bookAdBudget === 0
          }
        })
        queryRaiseStatus(tableList)
        state.tableList = tableList
      } else {
        state.tableList = []
      }

      nextTick(() => {
        if (planDataTableRef.value) {
          planDataTableRef.value.doLayout()
        }
      })
    }).catch(() => {
      state.loading = false
    })
  }

  const changePageIndex = (index) => {
    state.pageIndex = index
    getData()
  }

  const sortTable = ({ prop, order }) => {
    const orderType = {
      ascending: 'asc',
      descending: 'desc'
    }
    state.order = orderType[order] || ''
    state.sort = prop || ''
    state.pageIndex = 1
    getData()
  }

  const summaries = (param) => {
    const { columns } = param
    const data = JSON.parse(JSON.stringify(state.sumdata))
    const sums = []
    columns.forEach((column, index) => {
      const prop = column.property
      if (index === 1) {
        sums[index] = '合计'
        return
      }
      const twoDecimal = ['pv', 'clickNum', 'paperUseAmount', 'amount', 'newRoleMoney', 'newDeviceMoney', 'sumMoney', 'collectClickNum']
      if (!prop || ['deductRate'].includes(prop) || prop.indexOf('Retain') > -1 || prop.indexOf('Ltv') > -1) {
        sums[index] = ''
      } else if (prop.indexOf('Rate') > -1) {
        sums[index] = toPercentFixed(data[prop])
      } else if (['firstDayRoi', 'threeDayRoi', 'sevenDayRoi', 'createRole24hRoi', 'createRole24hAdjustedRoi', 'mDay1RoleRoi'].includes(prop)) {
        sums[index] = toPercentFixed(data[prop])
      } else if (twoDecimal.includes(prop)) {
        sums[index] = toThousands(data[prop])
      } else {
        sums[index] = data[prop]
      }
    })
    return sums
  }

  return {
    ...toRefs(state),
    planDataTableRef,
    getData,
    changePageIndex,
    sortTable,
    summaries
  }
}

/**
 * @description: 头条起量功能
 * @return {*}
 */
export const useTableRowToutiaoMaterialRaise = () => {
  const state = reactive({
    raiseStatusMap: {},
    raiseDrawerTitle: '',
    currMaterialRaisePlanId: 0,
    currMaterialRaiseRecordId: '',
    currMaterialRaiseDrawerType: TOUTIAO_RAISE_DRAWER_TYPE.NONE,
    materialRaiseDrawerVisible: false,
    toutiaoMaterialRaiseLoaded: false
  })

  const queryRaiseStatus = (tableList) => {
    const queryRaiseStatusParams = {
      planIds: tableList.filter(p => p.mediaId === MEDIAS.TOUTIAO).map(p => p.planId)
    }
    state.toutiaoMaterialRaiseLoaded = false
    if (queryRaiseStatusParams.planIds?.length) {
      getPtomPlanToutiaoRaiseStatus(queryRaiseStatusParams).then(data => {
        for (const item of data || []) {
          state.raiseStatusMap[item.planId] = item
        }
        state.toutiaoMaterialRaiseLoaded = true
      })
    }
  }

  const onOpenRaiseDrawer = ({ planId }, type) => {
    state.currMaterialRaisePlanId = planId
    state.currMaterialRaiseRecordId = state.raiseStatusMap[planId]?.currentRecordId || ''
    state.currMaterialRaiseDrawerType = type
    state.materialRaiseDrawerVisible = true
    if (type === TOUTIAO_RAISE_DRAWER_TYPE.CREATE) state.raiseDrawerTitle = '新建起量方案'
    if (type === TOUTIAO_RAISE_DRAWER_TYPE.REPORT) state.raiseDrawerTitle = '起量方案报告'
    if (type === TOUTIAO_RAISE_DRAWER_TYPE.DETAIL) state.raiseDrawerTitle = '起量方案详情'
  }

  return {
    ...toRefs(state),
    onOpenRaiseDrawer,
    queryRaiseStatus
  }
}

/**
 * @description: 表格列配置
 * @return {*}
 */
export const useColumns = () => {
  const tableCustomColsRef = ref(null)
  const state = reactive({
    tableColumns: [],
    toggleTableColLoading: false
  })

  const tableCol = computed(() => FIXED_DATA.concat(state.tableColumns.filter((ret) => ret.checked)))

  const onTableColsChange = async (cols) => {
    state.toggleTableColLoading = true
    await new Promise((resolve) => setTimeout(resolve, 100))
    state.tableColumns = cols
    state.toggleTableColLoading = false
  }

  const headerDragend = ({ n, o, column }) => {
    if (tableCustomColsRef.value) {
      tableCustomColsRef.value.updateTableColWidth(n, column)
    }
  }

  return {
    tableCustomColsRef,
    ...toRefs(state),
    tableCol,
    onTableColsChange,
    headerDragend
  }
}

/**
 * @description: 表格顶部操作按钮、开关、预警计划数量
 * @return {*}
 */
export const useOperationRowStates = () => {
  const state = reactive({
    showPlanWarningSettingModal: false,
    limitConsumption: false, // 过滤无消耗的计划
    allPlan: false, // 显示近1年新建全量计划
    earlyWarningPlanType: 0, // 预警计划类型
    earlyWarningPlanCount: 0
  })

  const queryEarlyWarningPlanCount = () => {
    // Fetch warning counts
    getPtomWarningPlanCounts().then((data) => {
      state.earlyWarningPlanCount = data || 0
    })
  }

  return {
    ...toRefs(state),
    queryEarlyWarningPlanCount
  }
}
